import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Share2, Copy, Check, ExternalLink, Trash2 } from 'lucide-react';

interface RechargeLink {
  id: string;
  token: string;
  client_name: string;
  client_number: string;
  is_active: boolean;
  created_at: string;
  expires_at: string;
  used_count: number;
  meter_id?: string;
}

const RechargeLinkDialog: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [existingLinks, setExistingLinks] = useState<RechargeLink[]>([]);
  const [userProfile, setUserProfile] = useState<{ first_name: string; last_name: string; client_number: string } | null>(null);

  const fetchUserProfile = useCallback(async () => {
    if (!user) return;

    try {
      // Try users table first (RLS allows user to view their own profile)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name, client_number')
        .eq('id', user.id)
        .maybeSingle();

      if (!userError && userData) {
        setUserProfile(userData);
        return;
      }

      // Fallback: Edge function (demo-only) for users not in users table
      const { data: edgeData, error: edgeError } = await supabase.functions.invoke(
        'get-profile',
        {
          headers: { 'Content-Type': 'application/json' },
          body: { email: user.email || '' }
        }
      );

      if (edgeError) throw edgeError;
      if (edgeData) {
        setUserProfile({
          first_name: edgeData.first_name,
          last_name: edgeData.last_name,
          client_number: edgeData.client_number
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch user profile. Please try again.',
      });
    }
  }, [user, toast]);

  const fetchExistingLinks = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('recharge_links')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExistingLinks(data || []);
    } catch (error) {
      console.error('Error fetching recharge links:', error);
    }
  }, [user]);

  useEffect(() => {
    if (open && user) {
      fetchExistingLinks();
      fetchUserProfile();
    }
  }, [open, user, fetchExistingLinks, fetchUserProfile]);

  const generateLink = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Look up the user's meter to store on the link
      let userMeterId: string | null = null;
      const { data: userData } = await supabase
        .from('users')
        .select('apartment_id')
        .eq('id', user.id)
        .maybeSingle();

      if (userData?.apartment_id) {
        const { data: meterData } = await supabase
          .from('smart_meters')
          .select('id')
          .eq('apartment_id', userData.apartment_id)
          .maybeSingle();

        if (meterData?.id) {
          userMeterId = meterData.id;
        }
      }

      // First try direct insert (works when user has a valid Supabase session)
      if (userProfile) {
        const token = `${user.id.substring(0, 8)}-${Date.now()}`;
        const insertData: any = {
          user_id: user.id,
          token,
          client_name: `${userProfile.first_name} ${userProfile.last_name}`,
          client_number: userProfile.client_number,
        };
        if (userMeterId) {
          insertData.meter_id = userMeterId;
        }
        const { data, error, status } = await supabase
          .from('recharge_links')
          .insert(insertData)
          .select()
          .single();

        if (!error && data) {
          toast({
            title: 'Link Created',
            description: 'Your recharge link has been generated successfully.',
          });
          setExistingLinks([data, ...existingLinks]);
          return;
        }

        // If RLS blocks (no auth session), fall through to Edge Function
        if (status === 401 || (error && `${error.message}`.toLowerCase().includes('row-level security'))) {
          // proceed to edge fallback
        } else if (error) {
          throw error;
        }
      }

      // Fallback: use Edge Function (demo-only) which is allowlisted to the demo account
      const { data: edgeData, error: edgeError } = await supabase.functions.invoke('create-recharge-link', {
        body: { email: user.email || '' },
      });

      if (edgeError) throw edgeError;

      toast({
        title: 'Link Created',
        description: 'Your recharge link has been generated successfully.',
      });

      if (edgeData) {
        setExistingLinks([edgeData as RechargeLink, ...existingLinks]);
      }
    } catch (error) {
      console.error('Error generating link:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to generate recharge link. Please ensure you are logged in or try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  const deactivateLink = async (linkId: string) => {
    try {
      const { error } = await supabase
        .from('recharge_links')
        .update({ is_active: false })
        .eq('id', linkId);

      if (error) throw error;

      toast({
        title: 'Link Deactivated',
        description: 'The recharge link has been deactivated.',
      });

      setExistingLinks(existingLinks.filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Error deactivating link:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to deactivate link.',
      });
    }
  };

  const copyToClipboard = async (link: RechargeLink) => {
    const url = `${window.location.origin}/recharge/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      toast({
        title: 'Copied!',
        description: 'Link copied to clipboard.',
      });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to copy link.',
      });
    }
  };

  const openLink = (token: string) => {
    window.open(`${window.location.origin}/recharge/${token}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" size="lg">
          <Share2 className="w-5 h-5 mr-2" />
          Get Recharge Link
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Shareable Recharge Links</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                Generate a link that anyone can use to top up your account, even if they don't have the app.
              </p>
              <Button onClick={generateLink} disabled={loading} className="w-full">
                {loading ? 'Generating...' : 'Generate New Link'}
              </Button>
            </CardContent>
          </Card>

          {existingLinks.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Your Active Links</h3>
              {existingLinks.map((link) => (
                <Card key={link.id}>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{link.client_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Created: {new Date(link.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Expires: {new Date(link.expires_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Used: {link.used_count} times
                        </p>
                      </div>
                    </div>

                    <div className="bg-muted rounded p-2 font-mono text-xs break-all">
                      {`${window.location.origin}/recharge/${link.token}`}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(link)}
                        className="flex-1"
                      >
                        {copiedId === link.id ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLink(link.token)}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deactivateLink(link.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RechargeLinkDialog;